import { DOCUMENT } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { AfterViewInit, Directive, HostListener, Inject, Input, OnDestroy, Self } from "@angular/core";
import { ControlContainer, UntypedFormGroup, FormGroupDirective, NgForm } from "@angular/forms";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { extractErrorMessage } from "@bbwt/modules/data-service";
import { AppStorage } from "@bbwt/utils/app-storage";
import { equalDeep } from "@bbwt/utils/equal-deep";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { UserService } from "@main/users";
import { FormStateComponent } from "@mipro/components/form-state.component";
import {
    catchError, debounceTime, distinctUntilChanged, filter,
    finalize, from, Observable, of, Subscription, switchMap, tap
} from "rxjs";

@Directive({
    selector: "form[autoSave]",
    exportAs: "autoSave"
})
export class AutoSaveDirective implements AfterViewInit, OnDestroy {
    @Input() autoSave!: (form?: any) => Promise<any> | Observable<any>;
    @Input() useServerResponse = true;
    @Input() skipDistinct = false;
    public static autoSaveDirectiveInit = false;

    @HostListener("submit")
    onSubmit() {
        if (!this.isAutoSave) {
            return;
        }

        this._ignoredFieldModified = false;
        if (this._formState) {
            this._formState.state = { type: "idle" };
        }
    }

    @Input() set formState(formState: FormStateComponent) {
        this._formState = formState;
    }

    private _ignoredFieldModified = false;
    private _formState?: FormStateComponent;
    private autoSaveSubsscription!: Subscription;
    private refreshUserSubscription!: Subscription;
    private static readonly HasIgnoredFieldModifiedKey = "auto-save-has-ignored-field-modified";
    private activeEl: HTMLElement;

    constructor(
        @Self() @Inject(ControlContainer) private formDirective: NgForm | FormGroupDirective,
        private userService: UserService,
        private broadcastService: BroadcastService,
        private appOnlineStateService: AppOnlineStateService,
        @Inject(DOCUMENT) private document: Document
    ) {
        AutoSaveDirective.showHttpHandlerMessage = false;
    }

    ngAfterViewInit(): void {
        this.refreshUserSubscription = this.broadcastService.on(UserService.CurrentUserChangedEventName).subscribe(_ => {
            this.autoSaveSubsscription?.unsubscribe();
            this.setupForm(this.formDirective.form);
        });
        this.setupForm(this.formDirective.form);
        AutoSaveDirective.autoSaveDirectiveInit = true;
    }

    ngOnDestroy(): void {
        this.autoSaveSubsscription?.unsubscribe();
        this.refreshUserSubscription?.unsubscribe();
        AutoSaveDirective.autoSaveDirectiveInit = false;
    }

    registerState(stateComponent: FormStateComponent) {
        this._formState = stateComponent;
    }

    ignoredFieldModified() {
        if (!this.isAutoSave) {
            return;
        }

        this._ignoredFieldModified = true;

        if (this._formState) {
            this._formState.state = {
                type: "failure",
                errorMessage: "There have been changes made to field(s) that do not auto-save, please SAVE manually"
            };
        }
        AutoSaveDirective.showHttpHandlerMessage = true;
    }

    get hasIgnoredFieldModified() {
        return this._ignoredFieldModified;
    }

    static get showHttpHandlerMessage() {
        return AppStorage.getItemFromSession(AutoSaveDirective.HasIgnoredFieldModifiedKey) ?? false;
    }

    static set showHttpHandlerMessage(visible: boolean) {
        AppStorage.setItemForSession(AutoSaveDirective.HasIgnoredFieldModifiedKey, visible);
    }

    private get isAutoSave() {
        return (this.userService.currentUser?.autoSaveForms ?? false) && this.appOnlineStateService.isAppOnline;
    }

    private setupForm(formGroup: UntypedFormGroup) {
        const _isEqual = (p: unknown, c: unknown) => equalDeep(p, c, true);

        if (formGroup && this.autoSave && this.isAutoSave) {
            this.autoSaveSubsscription = formGroup.valueChanges
                .pipe(
                    debounceTime(1000),
                    distinctUntilChanged((prev, cur) => !this.skipDistinct && _isEqual(prev, cur)),
                    filter(_ => !this.hasIgnoredFieldModified && formGroup.valid),
                    tap(_ => this.notifySaving()),
                    switchMap(value => {
                        let resultHandled = false;

                        return this.doSave(value, formGroup).pipe(
                            tap(result => {
                                resultHandled = true;
                                if (result !== null) {
                                    this.notifySuccess();
                                }
                            }),
                            finalize(() => {
                                this.restoreForm(formGroup);
                                if (!resultHandled) {
                                    this.resetFormState();
                                }
                            })
                        );
                    })
                )
                .subscribe(saved => {
                    if (saved != null && this.useServerResponse) {
                        formGroup.patchValue(saved, { emitEvent: false });
                    }
                });
        }
    }

    private resetFormState() {
        if (this._formState) {
            this._formState.state = { type: "idle" };
        }
    }

    private doSave(value: any, formGroup: UntypedFormGroup) {
        if (this.document.activeElement instanceof HTMLElement) {
            this.activeEl = this.document.activeElement;
        }

        value = { ...value, __$autoSave$__: true }; // To be used by the auto save interceptor, IF this `value` is used by
                                                    // the underlying `autoSave` handler

        const awaitable = from(this.autoSave(value)).pipe(
            catchError((errorResponse: HttpErrorResponse) => this.handleError(errorResponse))
        );
        /**
         * Disable right after triggering the save. It's important we do afterwards as client code
         * might be accessing the form's value directly and won't get any value if it's disabled.
         */
        if (this.useServerResponse) {
            formGroup.disable({ emitEvent: false });
        }

        return awaitable;
    }

    private restoreForm(formGroup: UntypedFormGroup) {
        if (!this.useServerResponse) {
            return;
        }

        formGroup.enable({ emitEvent: false });
        if (this.activeEl) {
            setTimeout(() => this.activeEl.focus());
        }
    }

    private notifySaving() {
        if (!this._formState) {
            return;
        }
        this._formState.state = { type: "saving" };
    }

    private notifySuccess() {
        if (!this._formState || this._formState.state.type !== "saving") {
            return;
        }

        this._formState.state = { type: "success" };
    }

    private handleError(error: HttpErrorResponse) {
        this.notifyError(error);
        return of(null);
    }

    private notifyError(errorResponse: HttpErrorResponse) {
        if (!this._formState) {
            return;
        }

        const errorMessage = extractErrorMessage(errorResponse);
        this._formState.state = { type: "failure", errorMessage };
    }
}
